import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse, OaSheetDataType } from '@/shared/types';
import { Api, createApi } from '@reduxjs/toolkit/query/react';
import queryString from 'query-string';

type LookupType =
  | 'gender'
  | 'titles'
  | 'frequency'
  | 'lenders'
  | 'designation'
  | 'citizenship';
interface GetLookupOptionsParams {
  types?: LookupType[];
}

type LookupOptionsResponse = Record<
  LookupType,
  { label: string; value: any }[]
>;
export const coreAPI = createApi({
  reducerPath: 'coreAPI',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    fetchCountryByIP: builder.query<
      { data: { country: string; ip: string } },
      void
    >({
      query: () => '/api/core/user-country/',
    }),
    getForexRate: builder.query<
      ApiResponse<any>,
      {
        fromCurrency: string;
        toCurrency: string;
        date?: Date | string;
        amount?: number;
      }
    >({
      query: ({ fromCurrency, toCurrency, date, amount = 1 }) => {
        const searchParams = new URLSearchParams({
          from_currency: fromCurrency,
          to_currency: toCurrency,
          amount: amount.toString(),
        });

        if (date) {
          const formattedDate =
            typeof date === 'string' ? date : date.toISOString().split('T')[0];
          searchParams.append('date', formattedDate);
        }

        return {
          url: `/forex/convert?${searchParams.toString()}`,
        };
      },
    }),

    postFeedback: builder.mutation<ApiResponse<any>, FormData>({
      query: (body) => {
        return {
          url: '/v1/users/feedback',
          method: 'POST',
          body,
        };
      },
    }),
    getSearchResults: builder.query<
      ApiResponse<any>,
      { query: string; fetchAll?: boolean }
    >({
      query: ({ query, fetchAll = false }) => {
        const searchParams = new URLSearchParams({
          searchQuery: query,
          fetchAll: fetchAll.toString(),
        });
        return {
          url: `/global-search?${searchParams.toString()}`,
        };
      },
    }),
    getOptionsList: builder.query<ApiResponse<any>, GetLookupOptionsParams>({
      query: ({ types }) => {
        const query = queryString.stringify(
          { types },
          { arrayFormat: 'comma' },
        );
        return {
          url: `v1/meta/lookup?${query}`,
        };
      },
    }),

    updateOaSheetData: builder.mutation<
      ApiResponse<any>,
      { sheetId: string; label: keyof OaSheetDataType; value: string }
    >({
      query: (body) => {
        return {
          url: '/google-sheet/update-by-label',
          method: 'POST',
          body,
        };
      },
    }),
  }),
});

export const {
  useGetForexRateQuery,
  useFetchCountryByIPQuery,
  usePostFeedbackMutation,
  useGetSearchResultsQuery,
  useGetOptionsListQuery,
  useUpdateOaSheetDataMutation,
} = coreAPI;
